import type { TFunction } from "i18next";
import type { NamedGroupItem, NamedGroupTone } from "@/ui/molecules/NamedGroupCard";
import { roundHours } from "./addStaffUtils";
import type { MultiAddReview } from "./multiAddReview";

/** The kinds of card the review can show, in the order they stack. */
export const REVIEW_GROUP_KINDS = ["target", "employee_status", "pto", "long_day", "overtime", "clear"] as const;
export type ReviewGroupKind = (typeof REVIEW_GROUP_KINDS)[number];

/** One `NamedGroupCard` of the review, copy resolved. */
export type ReviewGroup = {
	kind: ReviewGroupKind;
	title: string;
	count: number;
	tone: NamedGroupTone;
	description: string | undefined;
	items: NamedGroupItem[];
	/** Names with a per-name tag get a full row each; bare names pack into columns. */
	layout: "list" | "columns";
};

type GroupOptions = {
	/** `useRevealWage()` — the overtime cost only shows to people who can see wages. */
	canViewWage: boolean;
	longShiftLimitHours: number;
};

/** The cost string the grid shows when there is no prediction. */
const NO_COST = "-";

const targetGroup = (review: MultiAddReview, t: TFunction): ReviewGroup | null => {
	const { target } = review;
	if (!target) return null;
	return {
		kind: "target",
		title: t("add.staff.review.group.target"),
		// The headline is how far over the target the add goes — the number the scheduler weighs.
		count: target.current - target.target,
		tone: "warning",
		description: t("add.staff.review.group.target.description", { location: target.location, current: target.current, target: target.target }),
		items: [],
		layout: "columns",
	};
};

const statusGroup = (review: MultiAddReview, t: TFunction): ReviewGroup | null => {
	if (review.employeeStatus.length === 0) return null;
	const items = review.employeeStatus.map(({ nurseId, name, status, endDate }) => {
		// The stored end date is exclusive — the first day back — hence "back", never "until".
		let tag = t("add.staff.reason.suspended");
		if (status === "OnLeave") tag = endDate ? t("add.staff.reason.on.leave.back", { date: endDate }) : t("add.staff.reason.on.leave");
		return { id: nurseId, label: name, tag, tagTone: "error" as const };
	});
	return {
		kind: "employee_status",
		title: t("add.staff.review.group.status"),
		count: items.length,
		tone: "error",
		description: undefined,
		items,
		layout: "list",
	};
};

const ptoGroup = (review: MultiAddReview, t: TFunction): ReviewGroup | null =>
	review.pto.length === 0
		? null
		: {
				kind: "pto",
				title: t("add.staff.review.group.pto"),
				count: review.pto.length,
				tone: "info",
				description: t("add.staff.badge.pto.tooltip"),
				items: review.pto.map(({ nurseId, name }) => ({ id: nurseId, label: name })),
				layout: "columns",
			};

const longDayGroup = (review: MultiAddReview, t: TFunction, { longShiftLimitHours }: GroupOptions): ReviewGroup | null =>
	review.longDay.length === 0
		? null
		: {
				kind: "long_day",
				title: t("add.staff.review.group.long.day"),
				count: review.longDay.length,
				tone: "warning",
				description: t("add.staff.badge.long.day.tooltip", { limit: roundHours(longShiftLimitHours) }),
				items: review.longDay.map(({ nurseId, name, hours }) => ({
					id: nurseId,
					label: name,
					tag: t("add.staff.badge.long.day", { hours: roundHours(hours) }),
					tagTone: "warning" as const,
				})),
				layout: "list",
			};

const overtimeTag = (hours: number, cost: string, t: TFunction, canViewWage: boolean): string => {
	const rounded = roundHours(hours);
	return canViewWage && cost !== NO_COST ? t("add.staff.review.overtime.cost", { hours: rounded, cost }) : t("add.staff.badge.overtime", { hours: rounded });
};

const overtimeGroup = (review: MultiAddReview, t: TFunction, { canViewWage }: GroupOptions): ReviewGroup | null =>
	review.overtime.length === 0
		? null
		: {
				kind: "overtime",
				title: t("add.staff.review.group.overtime"),
				count: review.overtime.length,
				tone: "warning",
				description: t("add.staff.review.group.overtime.description"),
				items: review.overtime.map(({ nurseId, name, hours, cost }) => ({
					id: nurseId,
					label: name,
					tag: overtimeTag(hours, cost, t, canViewWage),
					tagTone: "warning" as const,
				})),
				layout: "list",
			};

const clearGroup = (review: MultiAddReview, t: TFunction): ReviewGroup | null =>
	review.clear.length === 0
		? null
		: {
				kind: "clear",
				title: t("add.staff.review.group.clear"),
				count: review.clear.length,
				tone: "success",
				description: undefined,
				items: review.clear.map(({ nurseId, name }) => ({ id: nurseId, label: name })),
				layout: "columns",
			};

/**
 * The review's cards: one per warning kind that applies (a nurse can be in several), then the staff with
 * no warnings — so the scheduler sees the whole batch, not just its problems.
 */
export const buildReviewGroups = (review: MultiAddReview, t: TFunction, options: GroupOptions): ReviewGroup[] =>
	[
		targetGroup(review, t),
		statusGroup(review, t),
		ptoGroup(review, t),
		longDayGroup(review, t, options),
		overtimeGroup(review, t, options),
		clearGroup(review, t),
	].filter((group): group is ReviewGroup => group !== null);
